"use client";

import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";

import type { TelemetryAggregatePointDto } from "@/lib/api/types";
import {
  aggregatesToAlignedData,
  alignedFromMutable,
  sliceBufferToXViewport,
  toMutableBuffer,
  trimBufferStart,
  type MutableAlignedBuffer,
} from "@/modules/telemetry/utils/aggregates-to-aligned";
import { parseTelemetryStreamPoint } from "@/modules/telemetry/utils/ws-telemetry-parse";
import { getActiveRealtimeClient } from "@/store/realtime";
import { REALTIME_CHANNELS } from "@/store/realtime";
import { useThemeStore } from "@/store/theme-store";

const STROKES = ["#2563eb", "#ea580c", "#16a34a", "#9333ea", "#dc2626"];
const FILLS = ["rgba(37,99,235,0.16)", "rgba(234,88,12,0.16)", "rgba(22,163,74,0.16)", "rgba(147,51,234,0.16)", "rgba(220,38,38,0.16)"];

/** Above this point count, slice series to current x-scale (zoom/pan) before drawing. */
const VIEWPORT_SLICE_THRESHOLD = 480;

function formatTooltipTimestamp(value: number): string {
  return new Date(value * 1000).toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatTooltipValue(value: number): string {
  if (!Number.isFinite(value)) {
    return "--";
  }
  if (Math.abs(value) >= 1000) {
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  if (Math.abs(value) >= 1) {
    return value.toLocaleString(undefined, { maximumFractionDigits: 3 });
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: 6 });
}

function formatTooltipColor(color: string): string {
  return color.startsWith("color-mix") ? "#94a3b8" : color;
}

function resolvePlotWidth(element: HTMLDivElement | null, requestedWidth: number): number {
  const parentWidth = element?.parentElement?.getBoundingClientRect().width ?? 0;
  const nextWidth = Math.max(requestedWidth, parentWidth);
  return nextWidth > 0 ? Math.floor(nextWidth) : requestedWidth;
}

function resolveChartPalette(isDark: boolean) {
  if (isDark) {
    return {
      axisText: "#CBD5E1",
      axisLine: "rgba(148, 163, 184, 0.48)",
      gridLine: "rgba(148, 163, 184, 0.28)",
      primaryLine: "#60A5FA",
      primaryFill: "rgba(96, 165, 250, 0.14)",
    };
  }

  return {
    axisText: "#64748B",
    axisLine: "rgba(100, 116, 139, 0.38)",
    gridLine: "rgba(203, 213, 225, 0.72)",
    primaryLine: "#2563EB",
    primaryFill: "rgba(37, 99, 235, 0.12)",
  };
}

type TelemetryChartStyle = "line" | "area" | "bar" | "step" | "scatter";

function isXScaleZoomed(
  scale: { min?: number | null; max?: number | null } | undefined,
  full: { min: number; max: number } | null,
): boolean {
  if (!scale || !full) {
    return false;
  }

  const min = scale.min;
  const max = scale.max;
  if (min == null || max == null || !Number.isFinite(min) || !Number.isFinite(max)) {
    return false;
  }

  const epsilon = 0.001;
  return Math.abs(min - full.min) > epsilon || Math.abs(max - full.max) > epsilon;
}

function buildOpts(
  width: number,
  height: number,
  metricKeys: string[],
  chartStyle: TelemetryChartStyle,
  isDark: boolean,
  seriesLabels?: Record<string, string>,
): uPlot.Options {
  const palette = resolveChartPalette(isDark);
  const uPlotWithPaths = uPlot as typeof uPlot & {
    paths?: {
      stepped?: (opts?: Record<string, unknown>) => unknown;
      bars?: (opts?: Record<string, unknown>) => unknown;
    };
  };

  const series = metricKeys.map((metricKey, i) => {
    const label = seriesLabels?.[metricKey] || metricKey;
    const stroke = i === 0 ? palette.primaryLine : (STROKES[i % STROKES.length] as string);
    const fill = i === 0 ? palette.primaryFill : (FILLS[i % FILLS.length] as string);

    if (chartStyle === "area") {
      return {
        label,
        stroke,
        fill,
        width: 2,
      };
    }

    if (chartStyle === "scatter") {
      return {
        label,
        stroke: "transparent",
        width: 0,
        points: {
          show: true,
          size: 5,
          width: 1,
          stroke,
          fill,
        },
      };
    }

    if (chartStyle === "step") {
      return {
        label,
        stroke,
      width: 2,
        paths: uPlotWithPaths.paths?.stepped?.({ align: 1 }) as never,
      };
    }

    if (chartStyle === "bar") {
      return {
        label,
        stroke,
        fill,
        width: 1,
        points: {
          show: false,
        },
        paths: uPlotWithPaths.paths?.bars?.({ size: [0.72, 100] }) as never,
      };
    }

    return {
      label,
      stroke,
      width: 1.8,
    };
  });

  return {
    width,
    height,
    ms: 1e-3,
    cursor: {
      drag: {
        x: true,
        y: false,
        setScale: true,
      },
    },
    scales: {
      x: { time: true },
    },
    series: [
      {},
      ...series,
    ],
    axes: [
      {
        stroke: palette.axisText,
        grid: {
          stroke: palette.gridLine,
          width: 1,
        },
        ticks: {
          stroke: palette.axisLine,
          width: 1,
        },
        border: {
          stroke: palette.axisLine,
          width: 1,
        },
      },
      {
        size: 50,
        stroke: palette.axisText,
        grid: {
          stroke: palette.gridLine,
          width: 1,
        },
        ticks: {
          stroke: palette.axisLine,
          width: 1,
        },
        border: {
          stroke: palette.axisLine,
          width: 1,
        },
      },
    ],
    legend: {
      live: false,
    },
  };
}

export type TelemetryUPlotChartProps = {
  width: number;
  height: number;
  deviceId: string;
  metricKeys: string[];
  aggregatePoints: TelemetryAggregatePointDto[];
  /** Bumps when React Query fetch completes (stable chart refresh without referential deps on huge arrays). */
  dataRevision: number;
  liveEnabled: boolean;
  /** Cap live + zoom tail; keeps canvas ops bounded (conceptual millions handled server-side). */
  liveWindowPoints: number;
  chartStyle?: TelemetryChartStyle;
  seriesLabels?: Record<string, string>;
};

function TelemetryUPlotChartInner({
  width,
  height,
  deviceId,
  metricKeys,
  aggregatePoints,
  dataRevision,
  liveEnabled,
  liveWindowPoints,
  chartStyle = "line",
  seriesLabels,
}: TelemetryUPlotChartProps) {
  const theme = useThemeStore((state) => state.theme);
  const isDark = theme === "dark";
  const rootEl = useRef<HTMLDivElement | null>(null);
  const shellEl = useRef<HTMLDivElement | null>(null);
  const tooltipEl = useRef<HTMLDivElement | null>(null);
  const plotRef = useRef<uPlot | null>(null);
  const bufferRef = useRef<MutableAlignedBuffer | null>(null);
  const xFullScaleRef = useRef<{ min: number; max: number } | null>(null);
  const pendingLiveRef = useRef<{ t: number; values: number[] }[]>([]);
  const rafRef = useRef(0);
  const viewportRafRef = useRef(0);
  const lastViewportSigRef = useRef("");
  const pointsRef = useRef(aggregatePoints);
  const [isZoomed, setIsZoomed] = useState(false);
  pointsRef.current = aggregatePoints;

  const metricSig = metricKeys.join(",");

  function hideTooltip() {
    if (tooltipEl.current) {
      tooltipEl.current.style.opacity = "0";
    }
  }

  function updateTooltip(plot: uPlot) {
    const tooltip = tooltipEl.current;
    const shell = shellEl.current;
    if (!tooltip || !shell) {
      return;
    }

    const idx = plot.cursor.idx;
    if (idx == null || idx < 0) {
      hideTooltip();
      return;
    }

    const data = plot.data;
    const timestamps = data[0] as number[] | undefined;
    const timestamp = timestamps?.[idx];
    if (timestamp == null || !Number.isFinite(timestamp)) {
      hideTooltip();
      return;
    }

    const rows = metricKeys
      .map((metricKey, seriesIndex) => {
        const label = seriesLabels?.[metricKey] || metricKey;
        const values = data[seriesIndex + 1] as Array<number | null | undefined> | undefined;
        const rawValue = values?.[idx];
        const stroke = formatTooltipColor(
          seriesIndex === 0 ? resolveChartPalette(isDark).primaryLine : (STROKES[seriesIndex % STROKES.length] as string),
        );
        return {
          label,
          value: typeof rawValue === "number" ? rawValue : NaN,
          stroke,
        };
      })
      .filter((item) => Number.isFinite(item.value));

    if (rows.length === 0) {
      hideTooltip();
      return;
    }

    tooltip.innerHTML = [
      `<div style="font-size:11px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#f8fafc;opacity:0.88;margin-bottom:8px">${formatTooltipTimestamp(timestamp)}</div>`,
      ...rows.map(
        (item) =>
          `<div style="display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:center;column-gap:14px;min-width:220px;padding:3px 0">
            <span style="display:inline-flex;align-items:center;gap:9px;min-width:0;color:#e5eef8">
              <span style="width:9px;height:9px;border-radius:999px;background:${item.stroke};display:inline-block;box-shadow:0 0 0 2px rgba(15,23,42,0.18)"></span>
              <span style="font-size:13px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${item.label}</span>
            </span>
            <strong style="font-size:13px;font-weight:700;font-variant-numeric:tabular-nums;color:#ffffff;text-align:right">${formatTooltipValue(item.value)}</strong>
          </div>`,
      ),
    ].join("");

    const cursorLeft = typeof plot.cursor.left === "number" ? plot.cursor.left : 0;
    const cursorTop = typeof plot.cursor.top === "number" ? plot.cursor.top : 0;
    const shellRect = shell.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const left = Math.min(
      Math.max(cursorLeft + 20, 8),
      Math.max(8, shellRect.width - tooltipRect.width - 8),
    );
    const top = Math.min(
      Math.max(cursorTop + 20, 8),
      Math.max(8, shellRect.height - tooltipRect.height - 8),
    );

    tooltip.style.transform = `translate(${left}px, ${top}px)`;
    tooltip.style.opacity = "1";
  }

  function applyViewportSlice(plot: uPlot) {
    const buf = bufferRef.current;
    if (!buf || buf.x.length === 0) {
      return;
    }
    if (buf.x.length < VIEWPORT_SLICE_THRESHOLD) {
      return;
    }
    const xScale = plot.scales.x;
    if (!xScale) {
      return;
    }
    const min = xScale.min;
    const max = xScale.max;
    if (min == null || max == null || !Number.isFinite(min) || !Number.isFinite(max)) {
      return;
    }
    const sliced = sliceBufferToXViewport(buf, min, max, 16);
    const xsView = sliced[0] as number[];
    if (xsView.length === 0) {
      return;
    }
    const first = xsView[0];
    const last = xsView[xsView.length - 1];
    const sig = `${xsView.length}:${first}:${last}`;
    if (sig === lastViewportSigRef.current) {
      return;
    }
    lastViewportSigRef.current = sig;
    plot.setData(sliced, false);
  }

  function scheduleViewportSlice(plot: uPlot) {
    if (viewportRafRef.current !== 0) {
      cancelAnimationFrame(viewportRafRef.current);
    }
    viewportRafRef.current = requestAnimationFrame(() => {
      viewportRafRef.current = 0;
      applyViewportSlice(plot);
    });
  }

  useLayoutEffect(() => {
    plotRef.current?.destroy();
    plotRef.current = null;
    bufferRef.current = null;
    pendingLiveRef.current = [];
    lastViewportSigRef.current = "";

    const root = rootEl.current;
    const pts = pointsRef.current;
    if (!root || metricKeys.length === 0 || pts.length === 0) {
      return;
    }

    const aligned = aggregatesToAlignedData(pts, metricKeys);
    const xs = aligned[0] as number[];
    if (xs.length > 0) {
      const first = xs[0];
      const last = xs[xs.length - 1];
      if (first !== undefined && last !== undefined) {
        xFullScaleRef.current = { min: first, max: last };
        setIsZoomed(false);
      }
    }

    bufferRef.current = toMutableBuffer(aligned, metricKeys.length);
    const effectiveWidth = resolvePlotWidth(root, width);
    const base = buildOpts(effectiveWidth, height, metricKeys, chartStyle, isDark, seriesLabels);
    const opts: uPlot.Options = {
      ...base,
      hooks: {
        setCursor: [
          (u) => {
            updateTooltip(u);
          },
        ],
        setScale: [
          (u, key) => {
            if (key !== "x") {
              return;
            }
            setIsZoomed(isXScaleZoomed(u.scales.x, xFullScaleRef.current));
            scheduleViewportSlice(u);
          },
        ],
      },
    };

    const plot = new uPlot(opts, aligned, root);
    plotRef.current = plot;

    const onDbl = () => {
      const xr = xFullScaleRef.current;
      if (xr) {
        lastViewportSigRef.current = "";
        setIsZoomed(false);
        plot.setScale("x", xr);
      }
    };
    const onMouseLeave = () => hideTooltip();
    plot.root.addEventListener("dblclick", onDbl);
    plot.over.addEventListener("mouseleave", onMouseLeave);

    if ((bufferRef.current?.x.length ?? 0) >= VIEWPORT_SLICE_THRESHOLD) {
      scheduleViewportSlice(plot);
    }

    return () => {
      if (viewportRafRef.current !== 0) {
        cancelAnimationFrame(viewportRafRef.current);
        viewportRafRef.current = 0;
      }
      plot.root.removeEventListener("dblclick", onDbl);
      plot.over.removeEventListener("mouseleave", onMouseLeave);
      plot.destroy();
      plotRef.current = null;
    };
  }, [width, height, metricSig, dataRevision, chartStyle, seriesLabels, isDark]);

  useLayoutEffect(() => {
    const plot = plotRef.current;
    const root = rootEl.current;
    if (plot) {
      plot.setSize({ width: resolvePlotWidth(root, width), height });
    }
  }, [width, height]);

  useEffect(() => {
    function flushLive() {
      rafRef.current = 0;
      const plot = plotRef.current;
      const buf = bufferRef.current;
      if (!plot || !buf || pendingLiveRef.current.length === 0) {
        return;
      }
      for (const p of pendingLiveRef.current) {
        buf.x.push(p.t);
        for (let i = 0; i < metricKeys.length; i++) {
          const row = buf.ys[i];
          const v = p.values[i];
          if (row) {
            row.push(v !== undefined && !Number.isNaN(v) ? v : NaN);
          }
        }
      }
      pendingLiveRef.current = [];
      trimBufferStart(buf, liveWindowPoints);
      plot.setData(alignedFromMutable(buf), false);
      lastViewportSigRef.current = "";
      updateTooltip(plot);
      if (buf.x.length >= VIEWPORT_SLICE_THRESHOLD) {
        scheduleViewportSlice(plot);
      }
    }

    if (!liveEnabled || !deviceId) {
      return;
    }

    const client = getActiveRealtimeClient();
    if (!client) {
      return;
    }

    const handler = (msg: unknown) => {
      const pt = parseTelemetryStreamPoint(msg, deviceId, metricKeys);
      if (!pt) {
        return;
      }
      pendingLiveRef.current.push(pt);
      if (rafRef.current === 0) {
        rafRef.current = requestAnimationFrame(flushLive);
      }
    };

    const unsub = client.subscribe(REALTIME_CHANNELS.telemetry, handler);
    return () => {
      unsub();
      if (rafRef.current !== 0) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
    };
  }, [liveEnabled, deviceId, metricSig, metricKeys, liveWindowPoints]);

  if (metricKeys.length === 0 || pointsRef.current.length === 0) {
    return (
      <div className="flex h-[360px] items-center justify-center rounded border border-dashed border-foreground/20 text-sm text-foreground/55">
        No aggregate points for this range (check device / time span).
      </div>
    );
  }

  return (
    <div ref={shellEl} className="relative w-full min-w-0 rounded-control bg-[#f8fafc] text-slate-700 dark:bg-[#101827] dark:text-slate-200">
      {isZoomed ? (
        <button
          type="button"
          onClick={() => {
            const plot = plotRef.current;
            const xr = xFullScaleRef.current;
            if (!plot || !xr) {
              return;
            }
            lastViewportSigRef.current = "";
            setIsZoomed(false);
            plot.setScale("x", xr);
          }}
          className="absolute right-3 top-3 z-[3] rounded-full border border-slate-300 bg-white/95 px-3 py-1 text-micro font-semibold uppercase tracking-[0.12em] text-slate-700 shadow-sm backdrop-blur hover:border-slate-400 hover:bg-white dark:border-slate-600 dark:bg-slate-800/95 dark:text-slate-100 dark:hover:border-slate-500 dark:hover:bg-slate-800"
        >
          Reset zoom
        </button>
      ) : (
        <div className="pointer-events-none absolute bottom-3 right-3 z-[2] rounded-full border border-slate-200 bg-white/85 px-2.5 py-1 text-[0.65rem] font-medium uppercase tracking-[0.12em] text-slate-500 shadow-sm backdrop-blur dark:border-slate-700 dark:bg-slate-900/85 dark:text-slate-300">
          Drag to zoom
        </div>
      )}
      <div
        ref={tooltipEl}
        className="pointer-events-none absolute left-0 top-0 z-[4] rounded-2xl border border-card-border/65 bg-[rgba(15,23,42,0.82)] px-4 py-3 text-[0.75rem] leading-5 text-white shadow-[0_24px_60px_-24px_rgba(15,23,42,0.65)] backdrop-blur-md"
        style={{ opacity: 0, transform: "translate(-9999px,-9999px)", transition: "opacity 120ms ease" }}
      />
      <div ref={rootEl} className="telemetry-uplot-root w-full min-w-0" />
    </div>
  );
}

function chartPropsEqual(a: TelemetryUPlotChartProps, b: TelemetryUPlotChartProps): boolean {
  return (
    a.dataRevision === b.dataRevision &&
    a.width === b.width &&
    a.height === b.height &&
    a.deviceId === b.deviceId &&
    a.liveEnabled === b.liveEnabled &&
    a.liveWindowPoints === b.liveWindowPoints &&
    a.chartStyle === b.chartStyle &&
    JSON.stringify(a.seriesLabels ?? {}) === JSON.stringify(b.seriesLabels ?? {}) &&
    a.metricKeys.join(",") === b.metricKeys.join(",") &&
    a.aggregatePoints === b.aggregatePoints
  );
}

export const TelemetryUPlotChart = memo(TelemetryUPlotChartInner, chartPropsEqual);
